import { useCallback, useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import { toast } from "react-toastify";
import { Building2, Check, MapPin, X } from "lucide-react";

import { Button } from "components/ui/Button";
import { InputFactory } from "factories/InputFactory";
import { AddressFormFieldNames } from "shared/enums/addressFormFieldNames";
import { AddressDto } from "api/actor";
import {
  applyValidationErrorsToForm,
  createPersonFieldResolver,
  parseApiError,
} from "shared/utils/apiValidationErrors";

import { plateformeInputsConfig, PlateformeInputFieldName } from "../form/plateformeInputsConfig";
import { PlateformeFormFieldNames } from "../form/PlateformeFormFieldNames";
import { ConnectionMode, CorporateDto, updateCorporate } from "../api/corporateApi";
import { CodeEntrepriseField } from "../components/CodeEntrepriseField";
import { CardsSection } from "components/layout/Section/CardsSection";
import { corporateInfoValidationSchema } from "./corporateInfoValidation";

interface CorporateInfoFormProps {
  plateformeId: string;
  corporate: CorporateDto;
  onSaved: () => void;
  onCancel: () => void;
}

// Valeurs du formulaire : sous-ensemble « identité + coordonnées » des champs org.
type CorporateInfoValues = Record<string, unknown>;

const toInitialValues = (c: CorporateDto): CorporateInfoValues => ({
  [PlateformeFormFieldNames.Name]: c.name ?? '',
  [PlateformeFormFieldNames.Code]: c.code ?? '',
  [PlateformeFormFieldNames.ConnectionMode]: c.connectionMode ?? 'STANDARD',
  [PlateformeFormFieldNames.RegistrationNumber]: c.registrationNumber ?? '',
  [PlateformeFormFieldNames.Capital]: c.capital ?? 0,
  [PlateformeFormFieldNames.RcsCity]: c.rcsCity ?? '',
  [PlateformeFormFieldNames.RcsNumber]: c.rcsNumber ?? '',
  [PlateformeFormFieldNames.OriasNumber]: c.oriasNumber ?? '',
  [PlateformeFormFieldNames.Rcp]: c.rcp ?? '',
  [PlateformeFormFieldNames.Phone]: c.phone ?? '',
  [PlateformeFormFieldNames.Email]: c.email ?? '',
  [AddressFormFieldNames.Address]: c.address?.line1 ?? '',
  [AddressFormFieldNames.AdditionalAddress]: c.address?.line2 ?? '',
  [AddressFormFieldNames.PostalCode]: c.address?.zipCode ?? '',
  [AddressFormFieldNames.City]: c.address?.city ?? '',
  [AddressFormFieldNames.Country]: c.address?.country ?? 'FR',
  [AddressFormFieldNames.GooglePlaceId]: c.address?.googlePlaceId ?? '',
  [AddressFormFieldNames.Latitude]: c.address?.latitude ?? 0,
  [AddressFormFieldNames.Longitude]: c.address?.longitude ?? 0,
});

const buildAddressDto = (v: CorporateInfoValues): AddressDto => {
  const line1 = (v[AddressFormFieldNames.Address] as string) ?? '';
  const city = (v[AddressFormFieldNames.City] as string) ?? '';
  const country = (v[AddressFormFieldNames.Country] as string) ?? '';
  return {
    line1,
    line2: (v[AddressFormFieldNames.AdditionalAddress] as string) || '',
    zipCode: (v[AddressFormFieldNames.PostalCode] as string) ?? '',
    city,
    country,
    formattedAddress: `${line1},${city},${country}`,
    latitude: (v[AddressFormFieldNames.Latitude] as number) || undefined,
    longitude: (v[AddressFormFieldNames.Longitude] as number) || undefined,
    googlePlaceId: (v[AddressFormFieldNames.GooglePlaceId] as string) || undefined,
  };
};

/** Édition de l'identité + coordonnées de la corporate (un seul Enregistrer). */
export const CorporateInfoForm: React.FC<CorporateInfoFormProps> = ({
  plateformeId,
  corporate,
  onSaved,
  onCancel,
}) => {
  const [isSaving, setIsSaving] = useState(false);

  const renderField = useCallback((fieldName: PlateformeInputFieldName) => {
    return <InputFactory config={plateformeInputsConfig} fieldName={fieldName} />;
  }, []);

  const onSubmit = async (
    values: CorporateInfoValues,
    helpers: FormikHelpers<CorporateInfoValues>,
  ) => {
    setIsSaving(true);
    try {
      const response = await updateCorporate(plateformeId, {
        name: values[PlateformeFormFieldNames.Name] as string,
        code: values[PlateformeFormFieldNames.Code] as string,
        connectionMode: values[PlateformeFormFieldNames.ConnectionMode] as ConnectionMode,
        phone: values[PlateformeFormFieldNames.Phone] as string,
        registrationNumber: values[PlateformeFormFieldNames.RegistrationNumber] as string,
        capital: (values[PlateformeFormFieldNames.Capital] as number) ?? 0,
        rcsCity: values[PlateformeFormFieldNames.RcsCity] as string,
        rcsNumber: values[PlateformeFormFieldNames.RcsNumber] as string,
        oriasNumber: values[PlateformeFormFieldNames.OriasNumber] as string,
        rcp: (values[PlateformeFormFieldNames.Rcp] as string) || null,
        email: values[PlateformeFormFieldNames.Email] as string,
        address: buildAddressDto(values),
      });

      if (!response.isSuccess) {
        if (response.errorMessage) toast.error(response.errorMessage);
        return;
      }
      toast.success('Informations enregistrées');
      onSaved();
    } catch (error) {
      const parsed = await parseApiError(error);
      const matched = applyValidationErrorsToForm({
        validationErrors: parsed.validationErrors,
        setFieldError: helpers.setFieldError,
        setFieldTouched: helpers.setFieldTouched,
        resolveField: createPersonFieldResolver(),
      });
      if (parsed.message || matched > 0) {
        toast.error(parsed.message || 'Veuillez corriger les champs en erreur');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={toInitialValues(corporate)}
      validationSchema={corporateInfoValidationSchema}
      onSubmit={onSubmit}
    >
      {({ dirty }) => (
        <Form className="flex flex-col gap-6">
          <CardsSection
        icon={<Building2 className="w-5 h-5 text-primary" />}
        title="Identité"
      >
        <p className="-mt-2 text-sm text-gray-500">Informations légales de la société.</p>
            <div className="rounded-2xl border bg-white p-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-1">{renderField(PlateformeFormFieldNames.Name)}</div>
              <div className="col-span-1">
                <CodeEntrepriseField name={PlateformeFormFieldNames.Code} required />
              </div>
              <div className="col-span-1">{renderField(PlateformeFormFieldNames.ConnectionMode)}</div>
              <div className="col-span-1">{renderField(PlateformeFormFieldNames.RegistrationNumber)}</div>
              <div className="col-span-1">{renderField(PlateformeFormFieldNames.Capital)}</div>
              <div className="col-span-1">{renderField(PlateformeFormFieldNames.RcsCity)}</div>
              <div className="col-span-1">{renderField(PlateformeFormFieldNames.RcsNumber)}</div>
              <div className="col-span-1">{renderField(PlateformeFormFieldNames.OriasNumber)}</div>
              <div className="col-span-1">{renderField(PlateformeFormFieldNames.Rcp)}</div>
            </div>
            </div>
          </CardsSection>

          <CardsSection
        icon={<MapPin className="w-5 h-5 text-primary" />}
        title="Coordonnées"
      >
        <p className="-mt-2 text-sm text-gray-500">Adresse et contact de la société.</p>
            <div className="rounded-2xl border bg-white p-6">
            <div className="grid grid-cols-6 gap-4">
              <div className="col-span-6">{renderField(AddressFormFieldNames.Address)}</div>
              <div className="col-span-4">{renderField(AddressFormFieldNames.AdditionalAddress)}</div>
              <div className="col-span-2">{renderField(AddressFormFieldNames.PostalCode)}</div>
              <div className="col-span-2">{renderField(AddressFormFieldNames.City)}</div>
              <div className="col-span-2">{renderField(AddressFormFieldNames.Country)}</div>
              <div className="col-span-2">{renderField(PlateformeFormFieldNames.Phone)}</div>
              <div className="col-span-6">{renderField(PlateformeFormFieldNames.Email)}</div>
            </div>
            </div>
          </CardsSection>

          <div className="flex justify-end gap-3">
            <Button outline type="button" onClick={onCancel} disabled={isSaving}>
              <X data-slot="icon" />
              Annuler
            </Button>
            <Button color="primary" type="submit" disabled={!dirty || isSaving}>
              <Check data-slot="icon" />
              Enregistrer
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};
